import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, BookOpen } from "lucide-react";
import { format } from "date-fns";
import MarkdownEditor from "@/components/MarkdownEditor";

export default function KnowledgeTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/knowledge"); setItems(data); }
    catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const onSave = async (payload) => {
    try {
      if (editing?.article_id) await api.patch(`/knowledge/${editing.article_id}`, payload);
      else await api.post("/knowledge", payload);
      toast.success("Saved");
      setOpen(false); setEditing(null);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/knowledge/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">DrkCECO uses these articles to answer customer questions. Add anything you'd write in an FAQ.</div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="kb-new-btn" className="gap-2" onClick={() => setEditing({ title: "", body: "", tags: [], published: true })}>
              <Plus className="h-4 w-4" /> New article
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl">
            <DialogHeader><DialogTitle>{editing?.article_id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
            {editing && <ArticleForm value={editing} onChange={setEditing} onSave={onSave} />}
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-border/60 bg-card divide-y divide-border/40">
        {items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No articles yet. Add your first FAQ to teach DrkCECO.</div>}
        {items.map((a) => (
          <div key={a.article_id} className="p-4 flex items-start gap-3">
            <BookOpen className="h-4 w-4 text-primary mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-sm">{a.title}</div>
                {a.published ? <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-500">published</span>
                              : <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-border">draft</span>}
                {(a.tags || []).map((t) => <span key={t} className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-border/60 text-muted-foreground">{t}</span>)}
              </div>
              <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{a.body.slice(0, 240)}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Updated {format(new Date(a.updated_at), "PP")} · by {a.author_name}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => { setEditing(a); setOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(a.article_id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArticleForm({ value, onChange, onSave }) {
  const [tagsInput, setTagsInput] = useState((value.tags || []).join(", "));
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); onSave({ ...value, tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean) }); }}>
      <div>
        <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Title</Label>
        <Input data-testid="kb-title" value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })} required />
      </div>
      <div>
        <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5 block">Body (rich markdown)</Label>
        <MarkdownEditor value={value.body} onChange={(v) => onChange({ ...value, body: v })} minRows={14} />
      </div>
      <div>
        <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Tags (comma-separated)</Label>
        <Input data-testid="kb-tags" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="hosting, minecraft, billing" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={value.published} onChange={(e) => onChange({ ...value, published: e.target.checked })} />
        Published (visible to DrkCECO)
      </label>
      <div className="flex justify-end">
        <Button data-testid="kb-save-btn" type="submit">Save</Button>
      </div>
    </form>
  );
}
